import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";

function formatMixpanelCohortLabel(raw) {
  const s = String(raw || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    if (!Number.isNaN(dt.getTime())) {
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(dt);
    }
  }
  const m2 = s.match(/^(\d{4})-(\d{2})$/);
  if (m2) {
    const y = Number(m2[1]);
    const mo = Number(m2[2]) - 1;
    const dt = new Date(y, mo, 1);
    if (!Number.isNaN(dt.getTime())) {
      return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(dt);
    }
  }
  return s || "—";
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pct(n) {
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(2)}%`;
}

function median(values) {
  if (!Array.isArray(values) || values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) return (sorted[mid - 1] + sorted[mid]) / 2;
  return sorted[mid];
}

/**
 * Mixpanel-style table for retention "Line" visualization.
 * Columns: Series | Average | cohort-date buckets (same as x-axis).
 * Rows: one per series (currently overall only; breakdown-ready).
 */
export default function RetentionLineTable({
  cohorts = [],
  breakdowns = [],
  metric = "retention_rate",
  intervals = [],
  retentionGroupIndex = 0,
  seriesLabel = "Overall",
}) {
  const [summaryMode, setSummaryMode] = useState("average");
  const [summaryMenuOpen, setSummaryMenuOpen] = useState(false);
  const [summarySort, setSummarySort] = useState("asc");
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const intervalIdx =
    intervals.length > 0
      ? Math.min(Math.max(0, retentionGroupIndex), intervals.length - 1)
      : 0;

  const orderedCohorts = useMemo(() => {
    const rows = (cohorts || []).filter((c) => c && !c.isSummary);
    return [...rows].sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""))
    );
  }, [cohorts]);

  const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;

  const columnCells = useMemo(() => {
    // Columns are calendar buckets (cohort dates). Keep chronological order (Mixpanel-style).
    return orderedCohorts.map((c) => ({
      rawLabel: c.label || "—",
      label: formatMixpanelCohortLabel(c.label),
    }));
  }, [orderedCohorts]);

  const overallRow = useMemo(() => {
    const cells = orderedCohorts.map((c) => {
      const v = c?.values?.[intervalIdx];
      const n = safeNum(v);
      return {
        rawLabel: c.label || "—",
        label: formatMixpanelCohortLabel(c.label),
        cohortSize: safeNum(c.cohortSize) ?? 0,
        value: n ?? 0,
        isIncomplete:
          Array.isArray(c.isIncomplete) && c.isIncomplete[intervalIdx] === true,
      };
    });
    return { segment: seriesLabel, cells };
  }, [intervalIdx, orderedCohorts, seriesLabel]);

  const breakdownRows = useMemo(() => {
    if (!hasBreakdowns) return [];
    const labels = orderedCohorts.map((c) => String(c?.label || ""));

    return (breakdowns || []).map((bd) => {
      const map = new Map();
      (bd?.cohorts || []).forEach((c) => {
        const key = String(c?.label || "");
        if (key) map.set(key, c);
      });

      const cells = labels.map((label) => {
        const segCohort = map.get(label) || null;
        const denomRaw = segCohort?.cohortSize;
        const denom =
          typeof denomRaw === "number" && !Number.isNaN(denomRaw) ? denomRaw : parseInt(denomRaw, 10) || 0;
        const retainedRaw = Array.isArray(segCohort?.retained) ? segCohort.retained[intervalIdx] : 0;
        const retained = retainedRaw != null ? Number(retainedRaw) : 0;
        const isCompleteArr = Array.isArray(segCohort?.isComplete) ? segCohort.isComplete : [];
        const isIncomplete = isCompleteArr[intervalIdx] === false;
        const value = metric === "retention_rate" ? (denom > 0 ? (retained / denom) * 100 : 0) : retained;

        return {
          rawLabel: label || "—",
          label: formatMixpanelCohortLabel(label),
          cohortSize: denom,
          value: safeNum(value) ?? 0,
          isIncomplete,
        };
      });

      return { segment: bd?.segment || "(none)", cells };
    });
  }, [breakdowns, hasBreakdowns, intervalIdx, metric, orderedCohorts]);

  useEffect(() => {
    if (!summaryMenuOpen) return undefined;
    const updateMenuPos = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 196;
      const menuHeight = 292;
      const gap = 8;
      const maxLeft = Math.max(gap, window.innerWidth - menuWidth - gap);
      const clampedLeft = Math.max(gap, Math.min(rect.left, maxLeft));
      // Keep the popup above the header; clamp to viewport top so it remains fully visible.
      const computedTop = Math.max(gap, rect.top - menuHeight - gap);
      setMenuPos({
        top: computedTop,
        left: clampedLeft,
      });
    };
    updateMenuPos();
    const onDocMouseDown = (event) => {
      const t = event.target;
      if (menuRef.current && menuRef.current.contains(t)) return;
      if (triggerRef.current && triggerRef.current.contains(t)) return;
      setSummaryMenuOpen(false);
    };
    const onDocKeyDown = (event) => {
      if (event.key === "Escape") setSummaryMenuOpen(false);
    };
    const onWinScroll = () => updateMenuPos();
    const onWinResize = () => updateMenuPos();
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onDocKeyDown);
    window.addEventListener("scroll", onWinScroll, true);
    window.addEventListener("resize", onWinResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onDocKeyDown);
      window.removeEventListener("scroll", onWinScroll, true);
      window.removeEventListener("resize", onWinResize);
    };
  }, [summaryMenuOpen]);

  const summaryLabelMap = {
    average: "Average",
    sum: "Sum",
    min: "Min",
    max: "Max",
    median: "Median",
  };

  const computeSummary = (cells) => {
    const values = (cells || []).map((r) => Number(r.value) || 0);
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, v) => acc + v, 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const med = median(values);

    if (summaryMode === "sum") return sum;
    if (summaryMode === "min") return min;
    if (summaryMode === "max") return max;
    if (summaryMode === "median") return med;

    // Default: weighted average for retention rate, arithmetic average for users.
    if (metric === "retention_rate") {
      const denom = (cells || []).reduce((acc, r) => acc + (r.cohortSize || 0), 0);
      if (denom <= 0) return 0;
      const numer = (cells || []).reduce((acc, r) => acc + (r.value || 0) * (r.cohortSize || 0), 0);
      return numer / denom;
    }
    return sum / values.length;
  };

  const rowsForTable = useMemo(() => {
    const base = hasBreakdowns ? breakdownRows : [overallRow];
    const withSummary = base.map((r) => ({ ...r, summary: computeSummary(r.cells) }));
    const sorted =
      summarySort === "desc"
        ? [...withSummary].sort((a, b) => (b.summary || 0) - (a.summary || 0))
        : [...withSummary].sort((a, b) => (a.summary || 0) - (b.summary || 0));
    return sorted;
  }, [breakdownRows, hasBreakdowns, overallRow, summarySort, summaryMode, metric]);

  if (columnCells.length === 0 || rowsForTable.length === 0) {
    return (
      <div className="w-full p-4 text-center text-sm" style={{ color: theme.textMuted }}>
        No data available
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse" style={{ fontFamily: "Raleway, sans-serif" }}>
        <thead style={{ backgroundColor: "#fafafa" }}>
          <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
            <th
              className="text-left py-2 px-3 text-xs font-semibold uppercase whitespace-nowrap"
              style={{ color: theme.textMuted, borderRight: `1px solid ${theme.border}` }}
            >
              Retention
            </th>
            <th
              className="text-left py-2 px-3 text-xs font-semibold uppercase whitespace-nowrap"
              style={{ color: theme.textMuted, borderRight: `1px solid ${theme.border}`, position: "relative" }}
            >
              <div
                ref={triggerRef}
                role="button"
                tabIndex={0}
                aria-haspopup="menu"
                aria-expanded={summaryMenuOpen}
                onClick={() => setSummaryMenuOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSummaryMenuOpen((v) => !v);
                  }
                }}
                className="inline-flex items-center gap-1 cursor-pointer"
                style={{ color: theme.textMuted, fontWeight: 600, textTransform: "none" }}
                title="Summary options"
              >
                <span>{summaryLabelMap[summaryMode] || "Average"}</span>
                <svg width="10" height="10" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              {summaryMenuOpen && createPortal(
                <div
                  ref={menuRef}
                  role="menu"
                  style={{
                    position: "fixed",
                    top: menuPos.top,
                    left: menuPos.left,
                    width: 196,
                    backgroundColor: theme.bgRaised || "#ffffff",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 10,
                    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
                    zIndex: 9999,
                    padding: "8px 0",
                    fontFamily: "Raleway, sans-serif",
                  }}
                >
                  <div className="px-3 pb-1 text-[11px] font-semibold" style={{ color: theme.textMuted, textTransform: "none" }}>
                    Summary
                  </div>
                  {[
                    { key: "average", label: "Average" },
                    { key: "sum", label: "Sum" },
                    { key: "min", label: "Min" },
                    { key: "max", label: "Max" },
                    { key: "median", label: "Median" },
                  ].map((opt) => {
                    const active = summaryMode === opt.key;
                    return (
                      <div
                        key={opt.key}
                        role="menuitemradio"
                        aria-checked={active}
                        onClick={() => {
                          setSummaryMode(opt.key);
                          setSummaryMenuOpen(false);
                        }}
                        className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                        style={{
                          backgroundColor: active ? (theme.bgOverlay || "#f1f5f9") : "transparent",
                          color: active ? (theme.textPrimary || "#0f172a") : (theme.textSecondary || "#475569"),
                          fontWeight: active ? 600 : 500,
                          textTransform: "none",
                          borderLeft: active ? `2px solid ${theme.primary}` : "2px solid transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = theme.bgOverlay || "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {opt.label}
                      </div>
                    );
                  })}
                  <div className="mx-2 my-1" style={{ borderTop: `1px solid ${theme.border}` }} />
                  <div className="px-3 pb-1 text-[11px] font-semibold" style={{ color: theme.textMuted, textTransform: "none" }}>
                    Sort
                  </div>
                  {[
                    { key: "asc", label: "1 \u2192 100" },
                    { key: "desc", label: "100 \u2192 1" },
                  ].map((opt) => {
                    const active = summarySort === opt.key;
                    return (
                      <div
                        key={opt.key}
                        role="menuitemradio"
                        aria-checked={active}
                        onClick={() => {
                          setSummarySort(opt.key);
                          setSummaryMenuOpen(false);
                        }}
                        className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                        style={{
                          backgroundColor: active ? (theme.bgOverlay || "#f1f5f9") : "transparent",
                          color: active ? (theme.textPrimary || "#0f172a") : (theme.textSecondary || "#475569"),
                          fontWeight: active ? 600 : 500,
                          textTransform: "none",
                          borderLeft: active ? `2px solid ${theme.primary}` : "2px solid transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = theme.bgOverlay || "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {summaryLabelMap[summaryMode] || "Average"} {opt.label}
                      </div>
                    );
                  })}
                </div>,
                document.body
              )}
            </th>
            {columnCells.map((c, i) => (
              <th
                key={i}
                className="text-left py-2 px-3 text-xs font-semibold uppercase whitespace-nowrap"
                style={{
                  color: theme.textMuted,
                  borderRight: i < columnCells.length - 1 ? `1px solid ${theme.border}` : "none",
                  minWidth: 72,
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsForTable.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              style={{
                borderBottom: `1px solid ${theme.border}`,
                backgroundColor: "white",
              }}
            >
              <td
                className="py-2 px-3 text-sm whitespace-nowrap"
                style={{
                  color: theme.text,
                  borderRight: `1px solid ${theme.border}`,
                  fontWeight: 500,
                  minWidth: 160,
                }}
                title={row.segment}
              >
                {row.segment}
              </td>
              <td
                className="py-2 px-3 text-sm text-right whitespace-nowrap"
                style={{ color: theme.text, borderRight: `1px solid ${theme.border}`, fontWeight: 600 }}
              >
                {metric === "retention_rate" ? pct(row.summary) : formatNumber(row.summary)}
              </td>
              {(row.cells || []).map((c, i) => (
                <td
                  key={i}
                  className="py-2 px-3 text-sm text-right whitespace-nowrap"
                  style={{
                    color: theme.text,
                    borderRight: i < (row.cells || []).length - 1 ? `1px solid ${theme.border}` : "none",
                    opacity: c.isIncomplete ? 0.7 : 1,
                  }}
                  title={c.cohortSize ? `${formatNumber(c.cohortSize)} users` : undefined}
                >
                  {metric === "retention_rate" ? pct(c.value) : formatNumber(c.value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

